// Phase 6 — Track C T-C4. RequireAuth — the gate for authenticated routes.
//
// Wraps a route subtree. Behaviour matches the auth flow described in plan
// §2.5:
//
//   * status === 'loading'         — render a minimal centred Skeleton.
//                                    In practice AuthProvider already
//                                    blocks initial render until the
//                                    bootstrap me() resolves, so this
//                                    branch is rarely reached. It exists
//                                    as a belt-and-braces guard for any
//                                    future code path that re-mounts
//                                    AuthProvider.
//   * status === 'unauthenticated' — <Navigate> to /login, preserving
//                                    the current location in `?from=`.
//                                    `replace` so the back button doesn't
//                                    bounce the user back into the
//                                    protected route.
//   * status === 'authenticated'   — render <Outlet/>.

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext.js';
import { Skeleton } from '../../components/ui/Skeleton.js';

export function RequireAuth(): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-surface-base"
        role="status"
        aria-live="polite"
        aria-label="Loading"
      >
        <Skeleton className="h-10 w-40" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    const from = `${location.pathname}${location.search}`;
    const target = from === '/' || from.startsWith('/login')
      ? '/login'
      : `/login?from=${encodeURIComponent(from)}`;
    return <Navigate to={target} replace />;
  }

  return <Outlet />;
}
